<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.course.actions.image.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel'},
      { text: $t('components.course.actions.image.deleteExistingPhoto'), type: 'confirm', variant: 'danger', callback: handleDelete, validate: false },
      { text: $t('components.course.actions.image.upload'), type: 'confirm', callback: handleConfirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="mt-4 text-left ">
      <o-form v-if="!loading" ref="form">
        <o-field-group :label="$t('components.course.actions.image.imageLabel')" name="Image" class="block pb-3">
          <o-upload v-model="image" />
        </o-field-group>
      </o-form>
    </div>

    <template #loading>
      <div v-if="loading" class="w-full max-w-sm mx-auto">
        <o-progress v-if="progress" :progress="progress" :label="$t('components.course.actions.image.uploading')" />
        <o-loader v-else class="mx-auto" />
      </div>
    </template>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  OUpload,
  OProgress,
} from '@/components/elements'

interface Props {
  course: any
  portal?: string
}

interface Emits {
  (e: 'success'): void
  (e: 'update:course', course: any): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { $api, $notification, $i18n } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const image = ref<File | null>(null)
const loading = ref(false)
const progress = ref(0)

const upload = computed({
  get: () => {
    if (image.value instanceof File) {
      return image.value
    }
    return null
  },
  set: (value) => {
    image.value = value
  },
})

const info = computed(() => {
  return props.course
    ? $i18n.t('components.course.actions.image.infoEdit', { name: props.course.displayName })
    : $i18n.t('components.course.actions.image.infoGeneric')
})

const open = () => {
  image.value = null
  dialog.value.open()
}

const validate = () => {
  return !!image.value
}

const handleOnUploadProgress = (event: ProgressEvent) => {
  progress.value = Math.round((event.loaded / event.total) * 100)
}

const handleConfirm = async () => {
  loading.value = true

  try {
    const imageFile = upload.value
    const options = { onUploadProgress: (event: ProgressEvent) => handleOnUploadProgress(event) }

    const response = await $api.course.addPhoto(props.course, imageFile!, options.onUploadProgress)
    const course = response.data

    if (course) {
      $notification.toast({
        title: $i18n.t('components.course.actions.image.addedPhoto'),
        text: $i18n.t('components.course.actions.image.updatedImage', { name: props.course.displayName }),
        variant: 'success',
      })

      emit('success')
      emit('update:course', course)

      loading.value = false
      progress.value = 0

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleDelete = async () => {
  loading.value = true

  try {
    const deleteResponse = await $api.course.deletePhoto(props.course)
    const course = deleteResponse.data

    if (course) {
      $notification.toast({
        title: $i18n.t('components.course.actions.image.deletedPhoto'),
        text: $i18n.t('components.course.actions.image.deletedImageFor', { name: props.course.displayName }),
        variant: 'success',
      })

      emit('success')
      emit('update:course', course)

      loading.value = false

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

defineExpose({ open })
</script>
